import { useEffect, useState } from 'react';
import { X, Ship, Flag, Briefcase, Archive, RotateCcw, Pencil, FileText, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';

const cls = (s) => (s || '').toLowerCase().replace(/\s+/g, '-');

export default function CrewDetail({ crewId, onClose, onEdit, onChanged, onOpenCase }) {
  const [data, setData] = useState(null);
  const [warnings, setWarnings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => Promise.all([
    api(`/crew/${crewId}/cases`).then(setData),
    api(`/crew/${crewId}/warnings`).then(setWarnings),
  ]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [crewId]);

  const toggleArchive = async () => {
    setError('');
    setBusy(true);
    try {
      await api(`/crew/${crewId}/${data.crew.status === 'Archived' ? 'restore' : 'archive'}`, { method: 'POST' });
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadProfile = async () => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crew/${crewId}/profile.pdf`, { credentials: 'include' });
    if (!res.ok) { setError('Could not generate the crew summary.'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `FairWay-${(data?.crew?.name || 'crew').replace(/[^A-Za-z0-9]+/g, '-')}-crew-summary.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (!data) return null;
  const c = data.crew;
  const archived = c.status === 'Archived';
  const name = (c.first_name && c.last_name) ? `${c.first_name} ${c.last_name}` : c.name;

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()} data-testid="crew-detail-drawer">
        <div className="drawer-head">
          <div>
            <span className="eyebrow">CREW RECORD</span>
            <h2 data-testid="crew-detail-name">{name}</h2>
            <div className="drawer-meta">
              {c.crew_id && <span className="crew-id-badge">{c.crew_id}</span>}
              <span className={`status ${archived ? 'appealed' : 'resolved'}`} data-testid="crew-detail-status">{c.status}</span>
              <span className="muted">{c.position || c.rank}{c.department ? ` · ${c.department}` : ''}</span>
            </div>
          </div>
          <button type="button" className="icon-btn" data-testid="crew-detail-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="drawer-subactions">
          <button className="ghost-btn" data-testid="crew-detail-edit" onClick={() => onEdit?.(c)}><Pencil size={13} /> Edit record</button>
          <button className="ghost-btn" data-testid="crew-export-pdf" onClick={downloadProfile}><FileText size={13} /> Crew summary PDF</button>
          {archived ? (
            <button className="ghost-btn" data-testid="crew-restore-button" onClick={toggleArchive} disabled={busy}>
              <RotateCcw size={13} /> Restore to active
            </button>
          ) : (
            <button className="danger-btn" data-testid="crew-archive-button" onClick={toggleArchive} disabled={busy}>
              <Archive size={13} /> Archive crew member
            </button>
          )}
        </div>
        <div className="drawer-body stack">
          {error && <div className="error" data-testid="crew-detail-error">{error}</div>}
          <div className="crew-detail-facts">
            <small><Ship size={12} /> {c.vessel}</small>
            {c.nationality && <small><Flag size={12} /> {c.nationality}</small>}
            {c.hire_date && <small><Briefcase size={12} /> Since {c.hire_date}</small>}
            {c.completed_contracts > 0 && <small>{c.completed_contracts} completed contract{c.completed_contracts === 1 ? '' : 's'}</small>}
            {c.contract_end && <small>Contract ends {c.contract_end}</small>}
          </div>
          <div className="crew-stat-row" data-testid="crew-case-stats">
            <span><strong>{data.stats.total}</strong>Total cases</span>
            <span><strong>{data.stats.open}</strong>Open</span>
            <span><strong>{data.stats.resolved}</strong>Resolved</span>
            <span><strong>{data.stats.disciplinary}</strong>Disciplinary</span>
            <span><strong>{data.stats.grievance}</strong>Grievance</span>
          </div>
          {warnings?.mandatory_hearing_required && (
            <div className="cba-alert" data-testid="crew-mandatory-hearing">
              <AlertTriangle size={15} />
              <span><b>{warnings.active_count} active written warnings in 12 months</b> — a mandatory hearing is required under CBA Art. 31.</span>
            </div>
          )}
          <div className="field"><span>WRITTEN WARNINGS · CBA ART. 31</span></div>
          {warnings && warnings.active.length === 0 && warnings.expired.length === 0 && (
            <div className="empty-inline" data-testid="crew-warnings-empty">No written warnings on file.</div>
          )}
          {[...(warnings?.active || []).map((w) => ({ ...w, live: true })), ...(warnings?.expired || [])].map((w) => (
            <div className="crew-warning" key={w.id} data-testid={`crew-warning-${w.id}`}>
              <div className="crew-case-top">
                <strong>{w.sanction}</strong>
                <span className={`status ${w.live ? 'hearing-scheduled' : 'resolved'}`}>{w.live ? 'Active' : 'Expired'}</span>
              </div>
              <small className="muted">
                Issued {w.issued_on} · valid until {w.expires_on} · case {w.case_id}
                {w.log_book_ref ? ` · log book ${w.log_book_ref}` : ''}
              </small>
              {w.department_head && <small className="muted">Issued by {w.department_head} ({w.issuing_authority}) · countersigned by {w.countersigned_by}</small>}
            </div>
          ))}
          <div className="field"><span>CASE HISTORY</span></div>          {data.cases.length === 0 && <div className="empty-inline" data-testid="crew-cases-empty">No cases recorded for this crew member.</div>}
          {data.cases.map((k) => (
            <button
              type="button"
              key={k.id}
              className="crew-case-item"
              data-testid={`crew-case-${k.id}`}
              onClick={() => onOpenCase?.(k.id)}
            >
              <div className="crew-case-top">
                <strong className="mono">{k.id}</strong>
                <span className={`status ${cls(k.status)}`}>{k.status}</span>
              </div>
              <div className="crew-case-mid">{k.case_type} · {k.category}</div>
              <p className="timeline-text">{k.summary}</p>
              <div className="crew-case-foot">
                <span className={`severity ${cls(k.severity)}`}>{k.severity}</span>
                <span className="muted">Opened {k.opened}{k.resolved_at ? ` · Resolved ${k.resolved_at}` : ''}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
